/**
 * The map list, in a dialog — what a dock opens.
 *
 * The list itself is shared with the board's own surfaces, so "manage my maps"
 * means the same thing wherever a therapist reaches it.
 */

import { useTranslation } from "react-i18next";
import { Network, Plus } from "lucide-react";

import { Modal } from "../../../../shared/ui/modal";
import Button from "../../../../shared/ui/button/Button";
import MapList from "./MapList";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    patientId?: string;
    couplePatientId?: string;
    groupPatientId?: string;
    onCreate?: () => void;
    /** Opens a board in a new tab rather than navigating. */
    openInNewTab?: boolean;
    /** Hands the chosen map to the caller instead of going anywhere. */
    onOpenMap?: (mapId: string) => void;
}

export default function MapListModal({
    isOpen,
    onClose,
    patientId,
    couplePatientId,
    groupPatientId,
    onCreate,
    openInNewTab = false,
    onOpenMap,
}: Props) {
    const { t } = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("systemicMap.list.title")}
            className="w-full max-w-3xl rounded-2xl bg-surface-overlay p-6"
        >
            {/* The dialog's own close button sits in the top-right corner, so
                the title keeps clear of it and the action goes on its own
                line. Side by side, the two collided. */}
            <div className="flex items-center gap-2 pr-12">
                <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/30"
                >
                    <Network className="h-5 w-5" />
                </span>
                <div>
                    <h2 className="text-lg font-bold text-content-primary">
                        {t("systemicMap.list.title")}
                    </h2>
                    <p className="text-xs text-content-tertiary">
                        {t("systemicMap.list.subtitle")}
                    </p>
                </div>
            </div>

            {onCreate && (
                <div className="mt-4">
                    <Button
                        size="sm"
                        startIcon={<Plus size={16} aria-hidden="true" />}
                        onClick={onCreate}
                    >
                        {t("systemicMap.list.create")}
                    </Button>
                </div>
            )}

            <div className="mt-4 max-h-[65vh] overflow-y-auto">
                {/* The dialog's own button is the one that creates; the list
                    below explains an empty caseload rather than offering the
                    action a second time. */}
                <MapList
                    patientId={patientId}
                    couplePatientId={couplePatientId}
                    groupPatientId={groupPatientId}
                    active={isOpen}
                    openInNewTab={openInNewTab}
                    onOpenMap={onOpenMap}
                />
            </div>
        </Modal>
    );
}
